import { useCallback, useState, useRef, MutableRefObject, useEffect } from 'react';
import FocusLock from 'react-focus-lock';

import { Popover, PopoverAnchor, PopoverContent, PopoverBody } from '@vimeo/bokeh';
import {
  MentionList,
  RichTextArea,
  RichTextAreaRefType,
  CommentContextFieldsType,
  ParsedMentionSuggestion,
  MentionPayload,
} from '@vimeo/comments-ui';
import useOnClickOutside from '@vimeo/ui/hooks/use-outside-click/useOnClickOutside';
import { useViewer } from '@vimeo/viewer';

import { useUserMentionSuggestions } from '../hooks/comments/use-mentions-and-team-permissions';
import { useVideoData } from '../hooks/use-video-data';
import {
  bpAddMentionee,
  bpStartComment,
  bpMentionImpression,
  bpEditComment,
} from '../utils/analytics';
import { buildMentionNodePayload, EMAIL_REGEX, getVideoIdFromClipRequestId } from '../utils/helper';
import { AnalyticsPropsType } from './types';

type CommentMentionModulePropsType = {
  autoFocus: boolean;
  richtext: string;
  isExpanded: boolean;
  placeholder: string;
  clipRequestId: string;
  isPublic: boolean;
  richtextAreaRef?: MutableRefObject<RichTextAreaRefType>;
  perPage?: number;
  page?: number;
  onFocus: () => void;
  onChange?: (editor: unknown) => void;
  setCommentText: (text: string) => void;
  analyticsProps?: AnalyticsPropsType;
  commentContextFields?: CommentContextFieldsType;
  customRichTextAreaStyles?: Record<string, unknown>;
  annotationTimeCode?: string;
  setIsMentionActive?: (value: boolean) => void;
  onSubmitComment?: (text: string) => void;
  isPostCommentLoading?: boolean;
  isDisabled?: boolean;
  showcaseId?: number;
  reviewId?: string;
};

export const CommentMentionModule = ({
  clipRequestId,
  perPage = 4,
  page = 1,
  richtextAreaRef,
  commentContextFields = {
    commentId: -1,
    existingReplyCounts: 0,
    isReply: false,
    isEdit: false,
    isTimeCoded: false,
  },
  analyticsProps,
  onFocus,
  isPostCommentLoading,
  showcaseId,
  reviewId,
  ...textAreaProps
}: CommentMentionModulePropsType) => {
  const viewer = useViewer();
  const teamOwnerId = viewer?.team?.ownerId;
  const { videoData } = useVideoData(clipRequestId, showcaseId, reviewId);
  const videoId = Number(getVideoIdFromClipRequestId(clipRequestId));

  const [showMentionPopover, setShowMentionPopover] = useState(false);
  const showMentionPopoverRef = useRef(showMentionPopover);
  showMentionPopoverRef.current = showMentionPopover;

  // To address stale closure issue with isPostCommentLoading
  const isPostCommentLoadingRef = useRef(isPostCommentLoading);
  isPostCommentLoadingRef.current = isPostCommentLoading;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  const { loadingMentionSuggestion, mentionSuggestionList, getMentionSuggestionItemsDebounced } =
    useUserMentionSuggestions(clipRequestId, perPage, page, showcaseId, reviewId);

  const currentMentioneeListRef = useRef<ParsedMentionSuggestion[]>([]);

  const mentionsPopupRef = useRef<HTMLElement>(null);
  const setShowMentionPopoverValue = useCallback(
    (value: boolean) => {
      setShowMentionPopover(value);
      textAreaProps?.setIsMentionActive?.(value);
    },
    [textAreaProps, setShowMentionPopover],
  );
  const hideMentionPopover = useCallback(
    () => setShowMentionPopoverValue(false),
    [setShowMentionPopoverValue],
  );
  const openMentionPopover = useCallback(() => {
    setShowMentionPopoverValue(true);
    if (analyticsProps) {
      let analytics = {
        ...analyticsProps,
        commentContextFields: {
          existing_reply_counts: commentContextFields.existingReplyCounts,
          is_reply: commentContextFields.isReply,
          comment_id: commentContextFields.commentId,
          comment_length: richtextAreaRef?.current?.commentLength() ?? 0,
        },
      };
      bpMentionImpression(
        videoId,
        videoData?.privacy.view,
        analytics,
        textAreaProps.isPublic,
        viewer,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mentionSuggestionListRef = useRef(mentionSuggestionList);
  mentionSuggestionListRef.current = mentionSuggestionList;

  const onMentionSelect = useCallback(
    (selectedIndex: number) => {
      const selectedMentionee = mentionSuggestionList[selectedIndex];
      if (!selectedMentionee) {
        return;
      }
      const payload = buildMentionNodePayload(selectedMentionee);
      richtextAreaRef?.current?.addMentionNode(payload);

      if (analyticsProps) {
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            existing_reply_counts: commentContextFields.existingReplyCounts,
            is_reply: commentContextFields.isReply,
            comment_id: commentContextFields.commentId,
            comment_length: richtextAreaRef?.current?.commentLength() ?? 0,
            mentionee_email: selectedMentionee.email,
            mentionee_id: selectedMentionee.userId,
            is_pending: selectedMentionee.isPending,
            mentionee_permission: null,
          },
        };
        bpAddMentionee(
          videoId,
          videoData?.privacy.view,
          videoData?.uploader.link,
          textAreaProps.isPublic,
          analytics,
          viewer,
        );
      }
      return;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mentionSuggestionList],
  );

  const findEmailMentionee = useCallback((mentioneeEmail: string) => {
    return (mentionSuggestionListRef.current as ParsedMentionSuggestion[]).find(
      ({ email }) => email === mentioneeEmail,
    );
  }, []);

  const onKeyUpHandler = useCallback(
    (mentionQuery: string, _view: unknown, event: KeyboardEvent) => {
      if (mentionQuery.length && event.code === 'Space') {
        const matches = mentionQuery.match(EMAIL_REGEX);

        if (matches?.length) {
          const emailMentionee = findEmailMentionee(mentionQuery);
          if (!emailMentionee) return;

          const payload = buildMentionNodePayload(emailMentionee);
          richtextAreaRef?.current?.addMentionNode(payload);
          if (analyticsProps) {
            let analytics = {
              ...analyticsProps,
              commentContextFields: {
                existing_reply_counts: commentContextFields.existingReplyCounts,
                is_reply: commentContextFields.isReply,
                comment_id: commentContextFields.commentId,
                comment_length: richtextAreaRef?.current?.commentLength() ?? 0,
                mentionee_email: emailMentionee.email,
                mentionee_id: emailMentionee.userId,
                is_pending: emailMentionee.isPending,
                mentionee_permission: null,
              },
            };
            bpAddMentionee(
              videoId,
              videoData?.privacy.view,
              videoData?.uploader.link,
              textAreaProps.isPublic,
              analytics,
              viewer,
            );
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findEmailMentionee],
  );

  const onKeyDownHandler = useCallback(
    (
      mentionQuery: string,
      _view: unknown,
      event: KeyboardEvent,
      onSubmitComment?: (text: string) => void,
    ) => {
      if (event.key === 'Enter' && !event.shiftKey && !showMentionPopoverRef.current) {
        event.preventDefault();
        if (event.isComposing) return;

        const richComment = richtextAreaRef?.current?.getCommentContent() || '';
        let commentLength = richtextAreaRef?.current?.commentLength();
        if (commentLength && !isPostCommentLoadingRef.current) {
          onSubmitComment?.(richComment);
        }
      }
      if (!showMentionPopoverRef.current) return;

      if (mentionQuery.length && mentionSuggestionListRef.current?.length) {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prevSelectedIndex) => {
            if (prevSelectedIndex === 0) {
              return mentionSuggestionListRef.current?.length - 1;
            } else {
              return prevSelectedIndex - 1;
            }
          });
          event.preventDefault();
          event.stopPropagation();
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex((prevSelectedIndex) => {
            if (prevSelectedIndex === mentionSuggestionListRef.current?.length - 1) {
              return 0;
            } else {
              return prevSelectedIndex + 1;
            }
          });
          event.preventDefault();
          event.stopPropagation();
        }

        if (event.key === 'Enter') {
          const mentionee = mentionSuggestionListRef.current[selectedIndexRef.current];
          if (!mentionee) return;

          const payload = buildMentionNodePayload(mentionee);
          richtextAreaRef?.current?.addMentionNode?.(payload);
          if (analyticsProps) {
            let analytics = {
              ...analyticsProps,
              commentContextFields: {
                existing_reply_counts: commentContextFields.existingReplyCounts,
                is_reply: commentContextFields.isReply,
                comment_id: commentContextFields.commentId,
                comment_length: richtextAreaRef?.current?.commentLength() ?? 0,
                mentionee_email: mentionee.email,
                mentionee_id: mentionee.userId,
                is_pending: mentionee.isPending,
                mentionee_permission: null,
              },
            };
            bpAddMentionee(
              videoId,
              videoData?.privacy.view,
              videoData?.uploader.link,
              textAreaProps.isPublic,
              analytics,
              viewer,
            );
          }
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedIndex],
  );

  const onMentioneeAdd = useCallback((mentionNode: MentionPayload) => {
    const mentioneeIdField = mentionNode.teamUserId ? 'teamUserId' : 'userId';

    const mentioneeSuggestion = mentionSuggestionListRef?.current.find(
      (mentionee) => mentionee[mentioneeIdField] === mentionNode[mentioneeIdField],
    );

    if (!mentioneeSuggestion) return;

    currentMentioneeListRef?.current.push(mentioneeSuggestion);
  }, []);

  const onMentioneeRemove = useCallback((userId: number) => {
    let isMentionRemoved = false;
    currentMentioneeListRef.current = currentMentioneeListRef?.current.filter((mentionee) => {
      const mentionUserId = mentionee?.userId ?? mentionee?.teamUserId;
      if (mentionUserId === userId && !isMentionRemoved) {
        isMentionRemoved = true;

        return false;
      }

      return true;
    });
  }, []);

  const trackCommentBoxFocus = () => {
    if (analyticsProps) {
      let analytics = {
        ...analyticsProps,
        commentContextFields: {
          existing_reply_counts: commentContextFields.existingReplyCounts,
          is_reply: commentContextFields.isReply,
          comment_id: commentContextFields.commentId,
          comment_length: richtextAreaRef?.current?.commentLength() ?? 0,
        },
      };
      if (commentContextFields.isEdit) {
        bpEditComment(
          `${videoId}`,
          videoData?.privacy.view,
          textAreaProps.isPublic,
          analytics,
          viewer,
          videoData?.uploader?.link,
          commentContextFields.isTimeCoded,
        );
      } else if (richtextAreaRef && !richtextAreaRef?.current.commentLength()) {
        // this should only fire when a new comment is being started - not when editor is refocused
        bpStartComment(
          videoId,
          videoData?.privacy.view,
          textAreaProps.isPublic,
          analytics,
          videoData?.uploader?.link,
          'text',
          false,
          viewer,
        );
      }
    }
  };

  const onTextAreaFocus = () => {
    onFocus();
    trackCommentBoxFocus();
  };

  useEffect(() => {
    if (mentionSuggestionList.length === 0) {
      hideMentionPopover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionSuggestionList]);

  useOnClickOutside(
    [mentionsPopupRef],
    () => {
      if (showMentionPopover) {
        setShowMentionPopoverValue(false);
      }
    },
    null,
    [showMentionPopover, setShowMentionPopoverValue],
  );

  // Function to determine if an element should be whitelisted - focus lock enabled
  const isWhitelisted = (node: HTMLElement) => {
    return (mentionsPopupRef.current && mentionsPopupRef.current.contains(node)) ?? false;
  };

  return (
    <Popover
      matchWidth
      isLazy
      returnFocusOnClose
      closeOnEsc
      isOpen={showMentionPopover}
      placement="top"
    >
      <PopoverContent padding="0" minWidth="0px" ref={mentionsPopupRef}>
        <PopoverBody>
          <MentionList
            selectedIndex={selectedIndex}
            mentionSuggestions={mentionSuggestionList}
            isLoading={loadingMentionSuggestion}
            isOpen
            onMenuItemSelection={onMentionSelect}
            avatarSize="sm"
            iconSpacing="10"
            borderRadius="input-md"
          />
        </PopoverBody>
      </PopoverContent>
      <PopoverAnchor>
        <FocusLock persistentFocus disabled={!showMentionPopover} whiteList={isWhitelisted}>
          <RichTextArea
            {...textAreaProps}
            ref={richtextAreaRef}
            mentionExtensionItemCallback={getMentionSuggestionItemsDebounced}
            onMentioneeAdd={onMentioneeAdd}
            onMentioneeRemove={onMentioneeRemove}
            onMentionStart={openMentionPopover}
            onMentionsExit={hideMentionPopover}
            onKeyUpHandler={onKeyUpHandler}
            onKeyDownHandler={onKeyDownHandler}
            teamOwnerId={teamOwnerId}
            onFocus={onTextAreaFocus}
            commentContextFields={commentContextFields}
          />
        </FocusLock>
      </PopoverAnchor>
    </Popover>
  );
};
